import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ActionIcon,
  Box,
  Center,
  Container,
  Group,
  Image,
  Modal,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronLeft, IconChevronRight, IconX } from "@tabler/icons-react";

import { shuffleArray } from "@/utils";

const galleryImages = shuffleArray([
  "bed.jpg",
  "pool-night-bg.jpg",
  "hammock-fisheye.jpg",
  "pool-cushions.jpg",
  "pool-view.jpg",
  "photo0.jpg",
  "photo1.jpg",
  "photo2.jpg",
  "photo3.jpg",
  "photo4.jpg",
  "photo5.jpg",
  "photo6.jpg",
]);

const imageUrl = (name: string) => `/img/gallery/${name}`;

export function Gallery() {
  const { t } = useTranslation();
  const [active, setActive] = useState<number | null>(null);
  const [opened, { open, close }] = useDisclosure(false);

  const openAt = (index: number) => {
    setActive(index);
    open();
  };

  const next = () =>
    setActive((current) =>
      current === null ? 0 : (current + 1) % galleryImages.length,
    );
  const prev = () =>
    setActive((current) =>
      current === null
        ? 0
        : (current - 1 + galleryImages.length) % galleryImages.length,
    );

  return (
    <Container size="xl">
      <Stack gap="xl">
        <Stack gap="xs" align="center" ta="center">
          <Title order={1}>{t("menu.gallery")}</Title>
          <Text c="dimmed" maw={520}>
            Slow days, salt air and golden afternoons.
          </Text>
        </Stack>

        <SimpleGrid
          cols={{ base: 2, sm: 3, md: 4 }}
          spacing={{ base: "sm", sm: "md" }}
        >
          {galleryImages.map((image, index) => (
            <Box
              key={image}
              onClick={() => openAt(index)}
              style={{
                cursor: "pointer",
                borderRadius: "var(--mantine-radius-lg)",
                overflow: "hidden",
                aspectRatio: "1 / 1",
                position: "relative",
                transition: "transform 200ms ease, box-shadow 200ms ease",
              }}
              onMouseEnter={(event) => {
                event.currentTarget.style.transform = "translateY(-2px)";
                event.currentTarget.style.boxShadow =
                  "var(--mantine-shadow-md)";
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.transform = "translateY(0)";
                event.currentTarget.style.boxShadow = "none";
              }}
            >
              <Image
                src={imageUrl(image)}
                alt={`Villa Braou ${index + 1}`}
                h="100%"
                w="100%"
                fit="cover"
                loading="lazy"
              />
            </Box>
          ))}
        </SimpleGrid>

        <Modal
          opened={opened}
          onClose={close}
          fullScreen
          padding={0}
          withCloseButton={false}
          transitionProps={{ transition: "fade" }}
          styles={{
            body: { background: "rgba(8, 14, 25, 0.96)", minHeight: "100dvh" },
          }}
        >
          <Box style={{ position: "relative", minHeight: "100dvh" }}>
            <ActionIcon
              variant="filled"
              color="dark"
              size="xl"
              radius="xl"
              onClick={close}
              aria-label="Close"
              style={{ position: "fixed", top: 16, right: 16, zIndex: 5 }}
            >
              <IconX size={20} />
            </ActionIcon>

            <Group
              justify="space-between"
              align="center"
              style={{
                position: "fixed",
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                pointerEvents: "none",
                padding: "0 12px",
              }}
            >
              <ActionIcon
                variant="filled"
                color="dark"
                size="xl"
                radius="xl"
                onClick={prev}
                aria-label={t("common.images.preview")}
                style={{ pointerEvents: "auto" }}
              >
                <IconChevronLeft size={20} />
              </ActionIcon>
              <ActionIcon
                variant="filled"
                color="dark"
                size="xl"
                radius="xl"
                onClick={next}
                aria-label={t("common.images.preview")}
                style={{ pointerEvents: "auto" }}
              >
                <IconChevronRight size={20} />
              </ActionIcon>
            </Group>

            <Center style={{ minHeight: "100dvh", padding: "24px" }}>
              {active !== null && (
                <Image
                  src={imageUrl(galleryImages[active])}
                  alt={`Villa Braou ${active + 1}`}
                  fit="contain"
                  mah="92dvh"
                  maw="100%"
                  radius="md"
                />
              )}
            </Center>
          </Box>
        </Modal>
      </Stack>
    </Container>
  );
}

export default Gallery;
